import { inject, signal, Signal } from '@angular/core';
import { injectMutation, injectQuery, QueryClient } from '@tanstack/angular-query-experimental';

import {
  CreatePatientInvoiceableInput,
  GqlService,
  PatientProtocolType,
  UpdatePatientInvoiceableInput,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';
import { batchAPIRequest } from '@shared/utils';
import { gqlToPromise } from '@shared/utils/gql-to-promise';
import { Maybe } from '@shared/utils/utils';

const MAX_BATCH_SIZE = 50;

export const injectPatientInvoiceables = ({
  enabled = signal(true),
  protocolVersionId,
}: {
  enabled?: Signal<boolean>;
  protocolVersionId: Signal<Maybe<string>>;
}) => {
  const mainQuery = inject(MainQuery);
  const gqlService = inject(GqlService);

  const patientProtocolTypes = [
    PatientProtocolType.PATIENT_PROTOCOL_OTHER,
    PatientProtocolType.PATIENT_PROTOCOL_SCREEN_FAIL,
    PatientProtocolType.PATIENT_PROTOCOL_DISCONTINUED,
  ];

  return injectQuery(() => ({
    enabled: enabled(),
    queryFn: () => {
      return gqlToPromise(() =>
        gqlService.listPatientInvoiceables$({
          patient_protocol_types: patientProtocolTypes,
          patient_protocol_version_id: protocolVersionId(),
        })
      );
    },
    queryKey: ['trials', mainQuery.trialKey(), 'patient-invoiceables', protocolVersionId()],
  }));
};

export const injectBatchCreatePatientInvoiceables = () => {
  const gqlService = inject(GqlService);
  const overlayService = inject(OverlayService);

  return injectMutation(() => ({
    mutationFn: async (params: CreatePatientInvoiceableInput[]) => {
      const { data, errors } = await batchAPIRequest(
        params,
        (input) => gqlService.batchCreatePatientInvoiceables$(input),
        MAX_BATCH_SIZE
      );

      if (errors.length) {
        throw errors;
      }

      return data;
    },
    onError: (error: string[]) => {
      overlayService.error(error);
    },
  }));
};

export const injectBatchUpdatePatientInvoiceables = () => {
  const gqlService = inject(GqlService);
  const overlayService = inject(OverlayService);

  return injectMutation(() => ({
    mutationFn: async (params: UpdatePatientInvoiceableInput[]) => {
      const { data, errors } = await batchAPIRequest(
        params,
        (input) => gqlService.batchUpdatePatientInvoiceables$(input),
        MAX_BATCH_SIZE
      );

      if (errors.length) {
        throw errors;
      }

      return data;
    },
    onError: (error: string[]) => {
      overlayService.error(error);
    },
  }));
};

export const refetchPatientInvoiceables = () => {
  const mainQuery = inject(MainQuery);
  const queryClient = inject(QueryClient);

  return () => {
    void queryClient.invalidateQueries({
      queryKey: ['trials', mainQuery.trialKey(), 'patient-invoiceables'],
    });
  };
};
